import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import {
  InboxIcon,
  MagnifyingGlassIcon,
  DocumentIcon,
  FolderIcon,
  UserGroupIcon,
  CalendarIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  ExclamationTriangleIcon,
  WifiIcon,
  LockClosedIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

/**
 * EmptyState Component
 *
 * Polished empty state component with illustrations,
 * helpful messages, and action buttons.
 *
 * @example
 * <EmptyState
 *   icon={InboxIcon}
 *   title="No messages"
 *   description="You don't have any messages yet."
 *   action={{ label: "Send a message", onClick: () => {} }}
 * />
 */

const iconBackgrounds = {
  default: 'bg-gray-100',
  primary: 'bg-wrg-blue-100',
  success: 'bg-success-100',
  warning: 'bg-warning-100',
  error: 'bg-error-100',
  info: 'bg-info-100',
};

const iconColors = {
  default: 'text-gray-400',
  primary: 'text-wrg-blue-500',
  success: 'text-success-500',
  warning: 'text-warning-500',
  error: 'text-error-500',
  info: 'text-info-500',
};

const sizes = {
  sm: {
    wrapper: 'py-8 px-4',
    iconWrapper: 'size-12 mb-3',
    icon: 'size-6',
    title: 'text-base',
    description: 'text-sm',
    button: 'px-3 py-1.5 text-sm',
  },
  md: {
    wrapper: 'py-12 px-6',
    iconWrapper: 'size-16 mb-4',
    icon: 'size-8',
    title: 'text-lg',
    description: 'text-sm',
    button: 'px-4 py-2 text-sm',
  },
  lg: {
    wrapper: 'py-16 px-8',
    iconWrapper: 'size-20 mb-5',
    icon: 'size-10',
    title: 'text-xl',
    description: 'text-base',
    button: 'px-5 py-2.5 text-base',
  },
};

const EmptyState = forwardRef(
  (
    {
      icon: Icon,
      title,
      description,
      action,
      secondaryAction,
      variant = 'default', // 'default', 'primary', 'success', 'warning', 'error', 'info'
      size = 'md',
      illustration, // Custom illustration component
      className,
      animate = true,
      children,
      ...props
    },
    ref
  ) => {
    const sizeStyles = sizes[size];

    return (
      <div
        ref={ref}
        className={clsx(
          'flex flex-col items-center justify-center text-center',
          sizeStyles.wrapper,
          animate && 'animate-fade-in',
          className
        )}
        {...props}
      >
        {/* Custom illustration or icon */}
        {illustration ? (
          <div className="mb-4">{illustration}</div>
        ) : Icon ? (
          <div
            className={clsx(
              'rounded-full flex items-center justify-center',
              sizeStyles.iconWrapper,
              iconBackgrounds[variant]
            )}
          >
            <Icon className={clsx(sizeStyles.icon, iconColors[variant])} />
          </div>
        ) : null}

        {/* Title */}
        {title && (
          <h3
            className={clsx(
              'font-semibold text-gray-900',
              sizeStyles.title,
              description && 'mb-2'
            )}
          >
            {title}
          </h3>
        )}

        {/* Description */}
        {description && (
          <p className={clsx('text-gray-500 max-w-sm', sizeStyles.description)}>
            {description}
          </p>
        )}

        {/* Actions */}
        {(action || secondaryAction) && (
          <div className="flex items-center gap-3 mt-6">
            {secondaryAction && (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className={clsx(
                  'inline-flex items-center gap-2',
                  'font-medium rounded-lg',
                  'text-gray-700 hover:bg-gray-100',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400',
                  'transition-colors duration-150',
                  sizeStyles.button
                )}
              >
                {secondaryAction.icon && (
                  <span className="size-4">{secondaryAction.icon}</span>
                )}
                {secondaryAction.label}
              </button>
            )}
            {action && (
              <button
                type="button"
                onClick={action.onClick}
                className={clsx(
                  'inline-flex items-center gap-2',
                  'font-medium rounded-lg',
                  'bg-wrg-blue-500 text-white',
                  'hover:bg-wrg-blue-600',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-wrg-blue-500 focus-visible:ring-offset-2',
                  'transition-colors duration-150',
                  sizeStyles.button
                )}
              >
                {action.icon && (
                  <span className="size-4">{action.icon}</span>
                )}
                {action.label}
              </button>
            )}
          </div>
        )}

        {/* Additional content */}
        {children}
      </div>
    );
  }
);

EmptyState.displayName = 'EmptyState';

/**
 * Pre-built empty state variants
 */

const NoResults = forwardRef(({ searchTerm, onClear, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={MagnifyingGlassIcon}
    title="No results found"
    description={
      searchTerm
        ? `We couldn't find anything matching "${searchTerm}". Try a different search term.`
        : "We couldn't find what you're looking for. Try adjusting your filters."
    }
    secondaryAction={onClear ? { label: 'Clear search', onClick: onClear } : undefined}
    {...props}
  />
));

NoResults.displayName = 'NoResults';

const NoData = forwardRef(({ entityName = 'items', action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={InboxIcon}
    title={`No ${entityName}`}
    description={`You don't have any ${entityName} yet. Create your first one to get started.`}
    action={action}
    {...props}
  />
));

NoData.displayName = 'NoData';

const NoDocuments = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={DocumentIcon}
    title="No documents"
    description="Upload or create your first document to get started."
    action={action}
    {...props}
  />
));

NoDocuments.displayName = 'NoDocuments';

const NoFolders = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={FolderIcon}
    title="No folders"
    description="Create a folder to organize your content."
    action={action}
    {...props}
  />
));

NoFolders.displayName = 'NoFolders';

const NoMembers = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={UserGroupIcon}
    title="No team members"
    description="Invite team members to collaborate together."
    action={action}
    {...props}
  />
));

NoMembers.displayName = 'NoMembers';

const NoEvents = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={CalendarIcon}
    title="No upcoming events"
    description="There are no events scheduled. Create a new event to get started."
    action={action}
    {...props}
  />
));

NoEvents.displayName = 'NoEvents';

const NoTravelRequests = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={PaperAirplaneIcon}
    title="No travel requests"
    description="You haven't submitted any travel requests yet. Create your first request to get started."
    action={action}
    variant="primary"
    {...props}
  />
));

NoTravelRequests.displayName = 'NoTravelRequests';

const NoPendingApprovals = forwardRef(({ ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={InboxIcon}
    title="All caught up!"
    description="You have no pending approvals at this time. Nice work!"
    variant="success"
    {...props}
  />
));

NoPendingApprovals.displayName = 'NoPendingApprovals';

const NoRoomBlocks = forwardRef(({ action, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={BuildingOfficeIcon}
    title="No room blocks"
    description="Create a room block to manage hotel accommodations for an event."
    action={action}
    {...props}
  />
));

NoRoomBlocks.displayName = 'NoRoomBlocks';

const ErrorState = forwardRef(({ message, onRetry, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={ExclamationTriangleIcon}
    title="Something went wrong"
    description={message || "We encountered an error loading this content. Please try again."}
    action={onRetry ? { label: 'Try again', onClick: onRetry } : undefined}
    variant="error"
    {...props}
  />
));

ErrorState.displayName = 'ErrorState';

const OfflineState = forwardRef(({ onRetry, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={WifiIcon}
    title="You're offline"
    description="Please check your internet connection and try again."
    action={onRetry ? { label: 'Retry', onClick: onRetry } : undefined}
    variant="warning"
    {...props}
  />
));

OfflineState.displayName = 'OfflineState';

const AccessDenied = forwardRef(({ ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={LockClosedIcon}
    title="Access denied"
    description="You don't have permission to view this content. Contact your administrator for access."
    variant="error"
    {...props}
  />
));

AccessDenied.displayName = 'AccessDenied';

const ComingSoon = forwardRef(({ feature, ...props }, ref) => (
  <EmptyState
    ref={ref}
    icon={ClockIcon}
    title="Coming soon"
    description={feature ? `${feature} is currently under development. Check back soon!` : "This feature is currently under development. Check back soon!"}
    variant="info"
    {...props}
  />
));

ComingSoon.displayName = 'ComingSoon';

// Attach pre-built variants
EmptyState.NoResults = NoResults;
EmptyState.NoData = NoData;
EmptyState.NoDocuments = NoDocuments;
EmptyState.NoFolders = NoFolders;
EmptyState.NoMembers = NoMembers;
EmptyState.NoEvents = NoEvents;
EmptyState.NoTravelRequests = NoTravelRequests;
EmptyState.NoPendingApprovals = NoPendingApprovals;
EmptyState.NoRoomBlocks = NoRoomBlocks;
EmptyState.Error = ErrorState;
EmptyState.Offline = OfflineState;
EmptyState.AccessDenied = AccessDenied;
EmptyState.ComingSoon = ComingSoon;

export {
  EmptyState,
  NoResults,
  NoData,
  NoDocuments,
  NoFolders,
  NoMembers,
  NoEvents,
  NoTravelRequests,
  NoPendingApprovals,
  NoRoomBlocks,
  ErrorState,
  OfflineState,
  AccessDenied,
  ComingSoon,
};
export default EmptyState;
